import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';
import { t } from 'i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import ManageCollaboratorsOptions from './sections/ManageCollaboratorsOptions/ManageCollaboratorsOptions';
import BdsLinearProgressIndicator from '../../../shared/components/v3/BdsLinearProgressIndicator/BdsLinearProgressIndicator';
import SecondaryHero from '../../../shared/components/v3/Heros/SecondaryHero/SecondaryHero';
import { COLLABORATORS_HOME_ROUTE } from '../../../shared/constants/auth';
import { MANAGE_COLLABORATORS_OPTIONS } from '../../../shared/constants/collaborators';
import { CAN_SEE_MANAGE_COLLABORATORS_DEPENDENTS_OPTION_FG } from '../../../shared/constants/statsig';
import { PlansContext } from '../../../shared/context/plans/plansContext';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../shared/translations/manageCollaborators';
import { objectHasKey } from '../../../shared/utils/utils';

import '../ManageCollaborators.scss';

const ManageCollaboratorActions = () => {
  const [collaboratorOption, setCollaboratorOption] = useState<string>('');

  const { plans, loading } = useContext(PlansContext);

  const hasDependent = plans.some((p) => p.supportDependents);

  const navigate = useNavigate();
  const location = useLocation();

  const { method } = location.state ? (location.state as { method: string }) : { method: undefined };

  const { client } = useStatsigClient();
  const canInsuranceStartFG = useMemo(
    () => client?.checkGate(CAN_SEE_MANAGE_COLLABORATORS_DEPENDENTS_OPTION_FG),
    [client]
  );

  const showDependentOption = hasDependent && canInsuranceStartFG;

  const handleNextStep = useCallback(
    (option: string) => {
      if (!objectHasKey(option, MANAGE_COLLABORATORS_OPTIONS)) return;
      navigate(MANAGE_COLLABORATORS_OPTIONS[option].route);
    },
    [navigate]
  );

  const handleActionBack = () => {
    navigate(COLLABORATORS_HOME_ROUTE);
  };

  useEffect(() => {
    if (method) {
      setCollaboratorOption(method);
    }
  }, [method]);

  return (
    <div className="manage-collaborators-container">
      <SecondaryHero
        title={t(TRANSLATION_MANAGE_COLLABORATORS.hero.collaboratorsManagementSection.title, { count: 2 })}
        description={t(TRANSLATION_MANAGE_COLLABORATORS.hero.collaboratorsManagementSection.subtitle, { count: 2 })}
        color="surface-container-quaternary-light"
        bottomContent={<BdsLinearProgressIndicator variant="subtle" size="s" progress={5} />}
        backAction={handleActionBack}
      />
      <div className="manage-collaborators-content">
        <ManageCollaboratorsOptions
          optionSelected={collaboratorOption}
          setOption={setCollaboratorOption}
          handleNextClick={handleNextStep}
          hasDependent={showDependentOption}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default ManageCollaboratorActions;
